/**
 * Reusable Click-to-Call button — drop wherever a phone number appears.
 *
 * Usage:
 *   <CallButton phone={p.phone} entity_type="prospect" entity_id={p.id}
 *               entity_name={p.name} customer_name={p.name} />
 *
 * The button is disabled if `phone` is empty, and shows a toast + logs a
 * `call_logs` row via POST /workspaces/{ws}/calls/click-to-call.
 */
import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { PhoneCall, Loader2 } from "lucide-react";
import CallStatusDialog from "./CallStatusDialog";

export default function CallButton({
  phone,
  customer_name,
  entity_type,
  entity_id,
  entity_name,
  variant = "outline",
  size = "sm",
  className = "rounded-none",
  label = null,
  testid = "call-btn",
}) {
  const { activeWorkspaceId } = useAuth();
  const [busy, setBusy] = useState(false);
  const [callState, setCallState] = useState(null);   // { call_id, agent_number, customer_number, status, failure_reason }

  const dialable = !!phone && String(phone).replace(/\D/g, "").length >= 6;

  const dial = async () => {
    if (!activeWorkspaceId) return;
    if (!dialable) { toast.error("No phone number on record"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/calls/click-to-call`, {
        customer_number: phone,
        customer_name: customer_name || entity_name || null,
        entity_type: entity_type || null,
        entity_id: entity_id || null,
        entity_name: entity_name || null,
      });
      if (data.status === "failed") {
        toast.error(`Call failed: ${data.failure_reason || "MCUBE rejected the request"}`);
      }
      // Open the status dialog either way — user sees WHY it failed and
      // agent/customer numbers, and can dismiss to try again.
      setCallState({
        callId: data.call_id || data.id,
        agentNumber: data.agent_number,
        customerNumber: data.customer_number || phone,
        customerName: customer_name || entity_name,
        agentName: data.agent_name,
        initialStatus: data.status || "initiating",
        initialFailure: data.failure_reason || null,
      });
    } catch (e) {
      const msg = apiErrorMessage(e, "Couldn't place call");
      if (msg.toLowerCase().includes("not configured")) {
        toast.error(msg, {
          action: { label: "Configure", onClick: () => { window.location.href = "/app/settings?tab=integrations"; } },
        });
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={dial}
        disabled={!dialable || busy}
        className={className}
        data-testid={testid}
        title={dialable ? `Call ${phone}` : "No phone number"}
      >
        {busy
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : <PhoneCall className="w-3.5 h-3.5" />}
        {label && <span className="ml-1">{label}</span>}
      </Button>
      {callState && (
        <CallStatusDialog {...callState} workspaceId={activeWorkspaceId} onClose={() => setCallState(null)} />
      )}
    </>
  );
}
